<template>
  <v-card class="card-time d-flex flex-column w-100 h-100">
    <v-window v-model="window" class="h-100 pa-1">
      <v-window-item class="h-100">
        <div
          class="d-flex flex-column align-center justify-center h-100"
          style="flex: 1"
        >
          <span style="font-size: 2.8vh"> Próximas Horas </span>
          <div
            v-if="isCanvasReady"
            class="w-100 h-100 px-1"
            style="height: auto"
          >
            <canvas style="margin: 0px 0 0 -30px" id="graficoTempo"></canvas>
          </div>
          <div class="d-flex flex-grow-1 justify-center align-center" v-else>
            <v-progress-circular indeterminate></v-progress-circular>
            <p class="text-center pl-2" style="font-size: 2vh">
              Carregando previsão...
            </p>
          </div>
        </div>
      </v-window-item>
      <v-window-item class="h-100">
        <div class="d-flex flex-column align-center h-100">
          <span style="font-size: 2.8vh"> Próximos Dias </span>

          <div
            class="d-flex justify-center align-center w-100 flex-grow-1"
            style="gap: 5px; flex-wrap: wrap; padding: auto"
          >
            <div
              v-for="(day, index) in dadosClima?.forecast?.daily?.slice(0, 5)"
              :key="index"
              class="previsao text-center text-h6"
              :style="{
                flex: '1 1 calc(20% - 10px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: index < 4 ? '1px solid #000000' : '',
                padding: '5px',
                color: '#616161',
              }"
            >
              <p style="font-size: 6vh; margin-bottom: 5px">
                {{ getWeatherEmoji(day.weather[0].icon) }}
              </p>
              <p style="font-size: 3vh">{{ getNextFiveDays()[index] }}</p>
              <p style="font-size: 3vh">
                {{ day.temp.min.toFixed(0) }}° - {{ day.temp.max.toFixed(0) }}°
              </p>
            </div>
          </div>
        </div>
      </v-window-item>
    </v-window>
  </v-card>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from "vue";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Chart } from "chart.js/auto";

const props = defineProps<{
  dadosClima: any;
  chartData: any[];
  chartLabels: any[];
}>();

const isCanvasReady = ref(false);
const window = ref(0);
const length = ref(2);

let chart: Chart | null = null;

onMounted(() => {
  const time = 10000;

  setInterval(() => {
    if (length.value > 0) {
      next();
    }
  }, time);
});

function next() {
  window.value = window.value + 1 > length.value - 1 ? 0 : window.value + 1;
}

watch(
  [() => props.chartData, () => props.chartLabels],
  () => {
    if (props.chartData.length === 0 || props.chartLabels.length === 0) return;

    initializeChart();
    isCanvasReady.value = true;
  },
  { once: true, deep: true }
);

function getWeatherEmoji(iconCode: string) {
  const emojiMap: { [key: string]: string } = {
    "01d": "☀️",
    "01n": "🌙",
    "02d": "⛅",
    "02n": "☁️",
    "03d": "☁️",
    "03n": "☁️",
    "04d": "☁️",
    "04n": "☁️",
    "09d": "🌧️",
    "09n": "🌧️",
    "10d": "🌦️",
    "10n": "🌧️",
    "11d": "⛈️",
    "11n": "⛈️",
    "13d": "❄️",
    "13n": "❄️",
    "50d": "🌫️",
    "50n": "🌫️",
  };
  return emojiMap[iconCode] || "❓";
}

function getNextFiveDays() {
  const daysOfWeek = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  const today = new Date();
  const nextFiveDays = [];

  for (let i = 1; i <= 5; i++) {
    const futureDate = new Date(today);
    futureDate.setDate(today.getDate() + i);
    nextFiveDays.push(daysOfWeek[futureDate.getDay()]);
  }

  return nextFiveDays;
}

async function initializeChart() {
  await nextTick();

  const canvas = document.getElementById("graficoTempo") as HTMLCanvasElement;
  if (!canvas) {
    console.warn("Canvas com ID 'graficoTempo' não encontrado.");
    return;
  }

  const ctx = canvas.getContext("2d");

  let minValue = 0;
  let maxValue = 0;

  if (props.chartData && props.chartData.length > 0) {
    minValue = Math.min(...props.chartData);
    maxValue = Math.max(...props.chartData);
  }

  chart = new Chart(ctx!, {
    type: "line",
    data: {
      labels: props.chartLabels,
      datasets: [
        {
          data: props.chartData,
          backgroundColor: "#e2dfe6",
          fill: true,
          borderColor: "#424242",
          borderWidth: 3,
          tension: 0.3,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 50,
          bottom: 10,
          left: 80,
          right: 12,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          display: true,
          color: "black",
          font: function (context) {
            const width = context.chart.width;
            const size = Math.round(width / 50);

            return {
              weight: "bold",
              size: size,
            };
          },
          align: "end",
          anchor: "end",
          formatter: (value: number, context: any) => {
            const index = context.dataIndex;
            const iconCode =
              props.dadosClima?.forecast?.hourly?.[index]?.weather?.[0]?.icon;
            const emoji = getWeatherEmoji(iconCode);

            return ` ${emoji}\n${value}°C`;
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: "black",
            font: function (context) {
              const width = context.chart.width;
              const size = Math.round(width / 50);

              return {
                weight: "bold",
                size: size,
              };
            },
          },
          grid: {
            display: false,
          },
        },
        y: {
          min: minValue - 1,
          max: maxValue + 1,
          ticks: {
            display: false,
          },
          grid: {
            display: false,
            // drawBorder: false,
          },
          border: {
            display: false,
          },
        },
      },
    },
    plugins: [ChartDataLabels],
  });
}
</script>
